import { toast } from "@win-reality/react-ui";
import { useEffect } from "react";

import { ContactPointSideDataCard } from "@/components/players/ContactChart/Side/ContactPointSideDataCard";
import { ContactPointDataCard } from "@/components/players/ContactChart/Top/ContactPointDataCard";
import { HeatMapDataCard } from "@/components/players/HeatMapChart/DataCard";
import { LaunchChartDataCard } from "@/components/players/LaunchChartCard/LaunchChartDataCard";
import { SprayChartDataCard } from "@/components/players/SprayChartCard/SprayChartDataCard";
import { Spinner } from "@/components/ui/spinner";
import { usePlayerProfileStore } from "@/stores/player-profile";

import { useLatestGame } from "../../Home/hooks/useLatestGame";
import { calculateLastThirtyDaysDateRange } from "../../utils/calculateDateRange";

type HitterChartsProps = {
  playerExternalId: string;
};

const hitsAndOutsPitchTypes = [
  "Fastball",
  "Offspeed",
  "Curveball",
  "Slider",
  "Cutter",
  "Other ",
];

const offspeedBreakingBallPitchTypes = ["Fastball", "4S", "2S", "Sinker"];

export const HitterCharts = ({ playerExternalId }: HitterChartsProps) => {
  console.log(
    "playerExternalId getting passed to the hitter charts",
    playerExternalId,
  );
  const dateFrom = usePlayerProfileStore((state) => state.dateFrom);
  const dateTo = usePlayerProfileStore((state) => state.dateTo);
  const setDateFrom = usePlayerProfileStore((state) => state.setDateFrom);
  const setDateTo = usePlayerProfileStore((state) => state.setDateTo);
  const gameType = usePlayerProfileStore((state) => state.gameType);
  const vsHand = usePlayerProfileStore((state) => state.vsHand);

  const {
    data: latestGameData,
    isLoading: isLoadingLatestGameDefaultDate,
    error: latestGameDefaultDateError,
  } = useLatestGame({
    playerId: playerExternalId,
    playerType: "hitter",
    showNoDataMessage: false,
  });

  const dateRange = calculateLastThirtyDaysDateRange(
    latestGameData?.latestGame?.game_started_at ?? null,
    latestGameData?.latestPractice?.game_started_at ?? null,
  );

  const hasSelectedDates = !!dateFrom && !!dateTo;

  useEffect(() => {
    // If the user has not selected dates, set the date range to the last 30 days
    if (!hasSelectedDates && dateRange) {
      setDateFrom(dateRange.startDate);
      setDateTo(dateRange.endDate);
    }
  }, [hasSelectedDates, dateRange, setDateFrom, setDateTo]);

  const hasError = !!latestGameDefaultDateError;

  useEffect(() => {
    if (hasError) {
      toast(
        "Something went wrong while loading the hitter charts. Please try again.",
        { variant: "error" },
      );
    }
  }, [hasError]);

  if (isLoadingLatestGameDefaultDate) {
    return (
      <div className="flex flex-col h-full items-center justify-center gap-2 overflow-hidden p-2 mb-16">
        <Spinner className="h-12 w-12" />
        <p className="text-sm text-muted-foreground">
          Loading hitter charts...
        </p>
      </div>
    );
  }

  if (!hasSelectedDates) {
    return (
      <div className="flex flex-col h-full items-center justify-center gap-2 overflow-hidden p-2 mb-16">
        <p className="text-sm text-muted-foreground">
          Please select a period or date to visualize some data
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full items-center gap-2 overflow-hidden p-2">
      <div className="flex flex-col xl:flex-row gap-2 w-full">
        {gameType === "game" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 grid-rows-2 gap-2 w-full">
            <SprayChartDataCard
              title="All Batted Balls"
              playerExternalId={playerExternalId}
              startDate={dateFrom}
              endDate={dateTo}
              gameType={gameType}
              playerType="hitter"
              vsHandedness={vsHand}
              hitsAndOutsOnly={true}
              filters={{
                pitch_type: `in.(${hitsAndOutsPitchTypes.join(",")})`,
              }}
            />
            <SprayChartDataCard
              title="Hits"
              playerExternalId={playerExternalId}
              startDate={dateFrom}
              endDate={dateTo}
              gameType={gameType}
              playerType="hitter"
              vsHandedness={vsHand}
              hitsAndOutsOnly={true}
              filters={{
                is_hit: "eq.true",
                pitch_type: `in.(${hitsAndOutsPitchTypes.join(",")})`,
              }}
            />
            <SprayChartDataCard
              title="Outs"
              playerExternalId={playerExternalId}
              startDate={dateFrom}
              endDate={dateTo}
              gameType={gameType}
              playerType="hitter"
              vsHandedness={vsHand}
              hitsAndOutsOnly={true}
              filters={{
                is_out: "eq.true",
                pitch_type: `in.(${hitsAndOutsPitchTypes.join(",")})`,
              }}
            />
            <SprayChartDataCard
              title="Vs All Offspeed/Breaking Ball"
              playerExternalId={playerExternalId}
              startDate={dateFrom}
              endDate={dateTo}
              gameType={gameType}
              playerType="hitter"
              vsHandedness={vsHand}
              hitsAndOutsOnly={true}
              filters={{
                is_out: "eq.true",
                pitch_type: `not.in.(${offspeedBreakingBallPitchTypes.join(",")})`,
              }}
            />
            {dateFrom && dateTo && (
              <>
                <LaunchChartDataCard
                  title="LA vs EV by Pitch"
                  playerExternalId={playerExternalId}
                  startDate={dateFrom}
                  endDate={dateTo}
                  gameType={gameType}
                  playerType="hitter"
                  vsHandedness={vsHand}
                  chartType="launch"
                  maxSpeed={120}
                  unit="MPH"
                  filters={{
                    play_result:
                      "in.(Double,Error,Sacrifices,HomeRun,Out,Single,Triple)",
                  }}
                  groupBy={["pitch_type"]}
                />
                <LaunchChartDataCard
                  title="LA vs EV by Results"
                  playerExternalId={playerExternalId}
                  startDate={dateFrom}
                  endDate={dateTo}
                  gameType={gameType}
                  playerType="hitter"
                  vsHandedness={vsHand}
                  chartType="launch"
                  maxSpeed={120}
                  unit="MPH"
                  filters={{
                    play_result:
                      "in.(Double,Error,Sacrifices,HomeRun,Out,Single,Triple)",
                  }}
                  groupBy={["play_result", "is_hit", "is_out"]}
                />
              </>
            )}
            {dateFrom && dateTo && (
              <>
                <ContactPointDataCard
                  title="Point of Contact - Top"
                  playerExternalId={playerExternalId}
                  startDate={dateFrom}
                  endDate={dateTo}
                  gameType={gameType}
                  playerType="hitter"
                  vsHandedness={vsHand}
                  chartType="points_contact"
                  groupBy={["is_out", "is_hit"]}
                />
                <ContactPointSideDataCard
                  title="Point of Contact - Side"
                  playerExternalId={playerExternalId}
                  startDate={dateFrom}
                  endDate={dateTo}
                  gameType={gameType}
                  playerType="hitter"
                  vsHandedness={vsHand}
                  chartType="points_contact"
                  groupBy={["is_out", "is_hit"]}
                />
              </>
            )}
            {dateFrom && dateTo && (
              <>
                <HeatMapDataCard
                  chartType="max_exit_velocity_by_zone"
                  title="Max Exit Velocity by Zone"
                  playerExternalId={playerExternalId}
                  startDate={dateFrom}
                  endDate={dateTo}
                  gameType={gameType}
                  playerType="hitter"
                  vsHandedness={vsHand}
                />
                <HeatMapDataCard
                  chartType="avg_exit_velocity_by_zone"
                  title="Average Exit Velocity by Zone"
                  playerExternalId={playerExternalId}
                  startDate={dateFrom}
                  endDate={dateTo}
                  gameType={gameType}
                  playerType="hitter"
                  vsHandedness={vsHand}
                />
                <HeatMapDataCard
                  chartType="contact_rate_by_zone"
                  title="Contact Rate by Zone"
                  playerExternalId={playerExternalId}
                  startDate={dateFrom}
                  endDate={dateTo}
                  gameType={gameType}
                  playerType="hitter"
                  vsHandedness={vsHand}
                />
                <HeatMapDataCard
                  chartType="whiff_rate_by_zone"
                  title="Whiff Rate by Zone"
                  playerExternalId={playerExternalId}
                  startDate={dateFrom}
                  endDate={dateTo}
                  gameType={gameType}
                  playerType="hitter"
                  vsHandedness={vsHand}
                />
              </>
            )}
          </div>
        )}

        {gameType === "practice" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 grid-rows-2 gap-2 w-full">
            <SprayChartDataCard
              title="All Hits"
              playerExternalId={playerExternalId}
              startDate={dateFrom}
              endDate={dateTo}
              gameType={gameType}
              playerType="hitter"
              vsHandedness={vsHand}
              showLegend={false}
              // batting practice does not register the pitch type and play result needed to identify an event as a hit or an out
              hitsAndOutsOnly={false}
            />
            {dateFrom && dateTo && (
              <HeatMapDataCard
                chartType="max_exit_velocity_by_zone"
                title="Max Exit Velocity by Zone"
                playerExternalId={playerExternalId}
                startDate={dateFrom}
                endDate={dateTo}
                gameType={gameType}
                playerType="hitter"
                vsHandedness={vsHand}
              />
            )}
            {dateFrom && dateTo && (
              <ContactPointDataCard
                title="Point of Contact - Top"
                playerExternalId={playerExternalId}
                startDate={dateFrom}
                endDate={dateTo}
                gameType={gameType}
                playerType="hitter"
                vsHandedness={vsHand}
                chartType="points_contact"
              />
            )}

            {dateFrom && dateTo && (
              <>
                <LaunchChartDataCard
                  title="Launch VS Exit Velocity"
                  playerExternalId={playerExternalId}
                  startDate={dateFrom}
                  endDate={dateTo}
                  gameType={gameType}
                  playerType="hitter"
                  vsHandedness={vsHand}
                  chartType="launch"
                  maxSpeed={120}
                  unit="MPH"
                />
                <ContactPointSideDataCard
                  title="Point of Contact - Side"
                  playerExternalId={playerExternalId}
                  startDate={dateFrom}
                  endDate={dateTo}
                  gameType={gameType}
                  playerType="hitter"
                  vsHandedness={vsHand}
                  chartType="points_contact"
                />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
